import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import GlassCard from "../components/GlassCard";
import GoldButton from "../components/GoldButton";

const LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "hi", label: "हिंदी" },
];

export default function Register() {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [language, setLanguage] = useState(i18n.resolvedLanguage || "en");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError(t("auth.passwordHint"));
      return;
    }
    setLoading(true);
    const res = await register({
      email: email.trim(),
      password,
      name: name.trim(),
      language,
    });
    setLoading(false);
    if (!res.ok) {
      setError(
        res.status === 409
          ? t("auth.errorEmailTaken")
          : res.error || t("auth.errorGeneric"),
      );
      return;
    }
    // Sync UI language to user choice
    if (i18n.resolvedLanguage !== language) {
      i18n.changeLanguage(language);
    }
    navigate("/dashboard", { replace: true });
  };

  return (
    <section className="mx-auto flex max-w-md flex-col gap-6 pt-10">
      <GlassCard className="p-8">
        <h1
          className="font-display text-3xl text-white tracking-tight"
          data-testid="register-title"
        >
          {t("auth.registerTitle")}
        </h1>
        <p className="mt-2 text-sm text-white/60">
          {t("auth.registerSubtitle")}
        </p>

        <form
          onSubmit={onSubmit}
          className="mt-7 grid gap-4"
          data-testid="register-form"
        >
          <Field label={t("auth.name")}>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="register-name-input"
              className="nxa-input"
            />
          </Field>
          <Field label={t("auth.email")}>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email-input"
              className="nxa-input"
            />
          </Field>
          <Field label={t("auth.password")} hint={t("auth.passwordHint")}>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password-input"
              className="nxa-input"
            />
          </Field>

          <fieldset className="grid gap-2">
            <legend className="text-sm font-medium text-white/65">
              {t("auth.language")}
            </legend>
            <div
              className="grid grid-cols-3 gap-2"
              data-testid="register-language-group"
            >
              {LANGS.map((l) => (
                <button
                  type="button"
                  key={l.code}
                  onClick={() => setLanguage(l.code)}
                  data-testid={`register-language-${l.code}`}
                  className={
                    "rounded-lg border px-3 py-2 text-sm font-medium transition-colors " +
                    (language === l.code
                      ? "border-[#D4AF37]/60 bg-[#D4AF37]/15 text-[#F4D77A]"
                      : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.06]")
                  }
                  aria-pressed={language === l.code}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </fieldset>

          {error && (
            <div
              className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200"
              data-testid="register-error"
            >
              {error}
            </div>
          )}

          <GoldButton
            type="submit"
            disabled={loading}
            className="mt-2 w-full"
            data-testid="register-submit-btn"
          >
            {loading ? t("common.loading") : t("auth.submitRegister")}
          </GoldButton>

          <p className="mt-3 text-center text-sm text-white/55">
            {t("auth.hasAccount")}{" "}
            <Link
              to="/login"
              className="text-[#D4AF37] hover:text-[#F4D77A] transition-colors"
              data-testid="link-login"
            >
              {t("auth.signIn")}
            </Link>
          </p>
        </form>
      </GlassCard>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="grid gap-2 text-sm">
      <span className="text-white/65 font-medium">
        {label}
        {hint && <span className="ml-2 text-xs text-white/40">· {hint}</span>}
      </span>
      {children}
    </label>
  );
}
